import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import api from "../../api/axios";
import { linkChecklistItemToEntry } from "../../api/trackerApi";
import { normalizeProjectMembersResponse } from "../../utils/assigneeTeamFilter";
import { TRACKER_COLORS as C, MONO } from "../../constants/trackerUi";
import TrackerModal from "./TrackerModal";

const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * Turn a checklist point into real, tracked daily work.
 *
 * It goes through the Daily Notebook's own endpoint rather than creating
 * issues here: that path already builds a Story with one Task per assignee,
 * fires the notifications and emits the socket events, and a second copy of
 * that logic would drift. All this adds is the epic to hang the story under
 * and the requirement to link back to.
 *
 * Deliberately per-point. The checklist is meant to stay lightweight scoping
 * notes; only the points that are genuinely someone's job get promoted.
 */
export default function PromoteChecklistItem({ requirement, item, onClose }) {
  const qc = useQueryClient();
  const [assigneeId, setAssigneeId] = useState("");
  // Default to the requirement's target date, else today — the notebook
  // rejects a tagged entry with no ETA.
  const [dueDate, setDueDate] = useState(
    (requirement?.targetDate || "").slice(0, 10) || todayIso()
  );
  const [busy, setBusy] = useState(false);

  const { data: membersRaw } = useQuery({
    queryKey: ["project-members", requirement?.projectId],
    queryFn: () => api.get(`/projects/${requirement.projectId}/members`).then((r) => r.data),
    enabled: Boolean(requirement?.projectId),
  });
  const members = normalizeProjectMembersResponse(membersRaw);

  const submit = async () => {
    if (!assigneeId) {
      toast.error("Pick who this is for");
      return;
    }
    // The notebook requires an ETA whenever anyone is tagged.
    if (!dueDate) {
      toast.error("Set a date for it");
      return;
    }
    const member = members.find((m) => String(m.id) === String(assigneeId));
    setBusy(true);
    try {
      // The notebook resolves assignees from assignee_ids; the @name in the
      // body is what people read in the notebook line.
      const body = `${item.label}${member?.full_name ? ` - @${member.full_name}` : ""}`;
      const res = await api.post(`/projects/${requirement.projectId}/daily-notebook`, {
        log_date: todayIso(),
        body_text: body,
        due_date: dueDate,
        assignee_ids: [Number(assigneeId)],
        epic_id: requirement.epicIssueId || undefined,
        requirement_id: requirement.id,
      });

      const entry = res.data?.entry || res.data;
      if (entry?.id) {
        await linkChecklistItemToEntry({
          id: requirement.id,
          itemId: item.id,
          entryId: entry.id,
        });
      }

      qc.invalidateQueries({ queryKey: ["tracker"] });
      qc.invalidateQueries({ queryKey: ["daily-notebook"] });
      qc.invalidateQueries({ queryKey: ["board"] });
      qc.invalidateQueries({ queryKey: ["my-issues"] });
      toast.success(
        member?.full_name
          ? `Sent to Daily Notebook for ${member.full_name}`
          : "Sent to Daily Notebook"
      );
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not send it to the notebook");
    } finally {
      setBusy(false);
    }
  };

  const label = {
    display: "block",
    fontFamily: MONO,
    fontSize: "0.58rem",
    textTransform: "uppercase",
    color: C.inkFaint,
    marginBottom: 5,
  };
  const input = {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 8,
    border: `1px solid ${C.line}`,
    fontSize: "0.85rem",
    boxSizing: "border-box",
  };

  return (
    <TrackerModal
      title="Send to Daily Notebook"
      subtitle="It becomes a story with a task for whoever owns it, tracked like any other day's work."
      onClose={onClose}
      maxWidth={460}
      footer={
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onClose}
            style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.line}`, background: C.surface, cursor: "pointer", fontWeight: 600, fontSize: "0.82rem" }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", cursor: busy ? "not-allowed" : "pointer", fontWeight: 700, fontSize: "0.82rem", opacity: busy ? 0.7 : 1 }}
          >
            {busy ? "Sending…" : "Send to notebook"}
          </button>
        </div>
      }
    >
      <div
        style={{
          padding: "9px 11px",
          borderRadius: 8,
          background: C.sunken,
          border: `1px solid ${C.line}`,
          fontSize: "0.85rem",
          color: C.ink,
          marginBottom: 14,
        }}
      >
        {item.label}
      </div>

      <div style={{ marginBottom: 12 }}>
        <span style={label}>Who is doing it</span>
        <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} style={input}>
          <option value="">Choose someone…</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>
              {m.full_name || m.email}
            </option>
          ))}
        </select>
      </div>

      <div>
        <span style={label}>By when *</span>
        <input type="date" value={dueDate || ""} onChange={(e) => setDueDate(e.target.value)} style={input} />
      </div>
    </TrackerModal>
  );
}
